import { Button } from "@web/src/components/ui/button";
import { Loader } from "@web/src/components/ui/loader";
import { useStores } from "@web/src/stores/web-stores";
import Image from "next/image";
import { observer } from "mobx-react";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { analytics } from "@web/src/services/analytics";
import {
  DEFAULT_POST_LOGIN_PATH,
  authCallbackUrl,
  rememberPostLoginRedirect,
  sanitizeRedirectPath,
} from "@web/src/lib/auth-redirect";

function LoginPage() {
  const { authStore } = useStores();
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);
  // `authStore.isLoading` is shared with OAuth, so it can't drive this label.
  const [sendingLink, setSendingLink] = useState(false);

  /** Where the visitor came from — a shared setlist, usually. */
  const redirectTo = sanitizeRedirectPath(router.query.redirect);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    // `router.query` is empty until the router is ready, and sending someone
    // to the default destination is not something to get wrong by racing it.
    if (!router.isReady) return;
    if (authStore.isHydrated && authStore.isAuthenticated) {
      router.replace(redirectTo ?? DEFAULT_POST_LOGIN_PATH);
    }
  }, [
    authStore.isHydrated,
    authStore.isAuthenticated,
    redirectTo,
    router,
    router.isReady,
  ]);

  if (!mounted || !authStore.isHydrated) return <Loader text="Loading..." />;
  if (authStore.isAuthenticated) return <Loader text="Redirecting..." />;

  if (authStore.magicLinkSent) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-6 p-4">
        <div className="flex flex-col items-center gap-3">
          <Image
            src="/setlist-icon.png"
            alt="Setlist"
            width={80}
            height={80}
            className="rounded-2xl"
            priority
          />
          <h1 className="text-3xl font-bold">Check your email</h1>
          <p className="text-muted-foreground text-center max-w-xs">
            We sent a sign-in link to <strong>{email}</strong>. Open it on this
            device to continue. The link expires in 10 minutes.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => authStore.setMagicLinkSent(false)}
        >
          Use a different email
        </Button>
      </div>
    );
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSendingLink(true);
    try {
      rememberPostLoginRedirect(redirectTo);
      await authStore.sendMagicLink(email, authCallbackUrl(redirectTo));
      analytics.signedUp("magic-link");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not send sign-in link",
      );
    } finally {
      setSendingLink(false);
    }
  }

  async function handleOAuth(provider: "google" | "apple") {
    setError(null);
    analytics.loginStarted(provider, "login_page");
    rememberPostLoginRedirect(redirectTo);
    try {
      await authStore.signInWithOAuth(provider, authCallbackUrl(redirectTo));
    } catch (err) {
      setError(err instanceof Error ? err.message : "OAuth failed");
    }
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-8 p-4">
      <div className="flex flex-col items-center gap-3">
        <Image
          src="/setlist-icon.png"
          alt="Setlist"
          width={80}
          height={80}
          className="rounded-2xl"
          priority
        />
        <h1 className="text-3xl font-bold">Setlist</h1>
        <p className="text-muted-foreground text-center">
          Sign in to save and sync your setlists
        </p>
      </div>

      <div className="flex flex-col gap-3 w-full max-w-xs">
        <Button
          size="lg"
          variant="outline"
          onClick={() => handleOAuth("google")}
          disabled={authStore.isLoading}
          className="gap-3"
        >
          <GoogleIcon />
          Continue with Google
        </Button>

        <Button
          size="lg"
          variant="outline"
          onClick={() => handleOAuth("apple")}
          disabled={authStore.isLoading}
          className="gap-3"
        >
          <AppleIcon />
          Continue with Apple
        </Button>
      </div>

      <div className="flex items-center gap-3 w-full max-w-xs">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">or</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form
        onSubmit={handleEmailSubmit}
        className="flex flex-col gap-3 w-full max-w-xs"
      >
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        />
        <Button type="submit" size="lg" disabled={authStore.isLoading}>
          {sendingLink ? "Sending..." : "Email me a sign-in link"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          No password needed. New here? A link creates your account.
        </p>
      </form>

      {error && (
        <p className="text-sm text-destructive text-center max-w-xs">{error}</p>
      )}
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.909-2.259c-.806.54-1.837.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332A8.997 8.997 0 0 0 9.003 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.712A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.33z"
        fill="#FBBC05"
      />
      <path
        d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0A8.997 8.997 0 0 0 .96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
        fill="#EA4335"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 814 1000" fill="currentColor">
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57.8-155.5-127.4c-57.8-80.8-103.7-205.7-103.7-324.2 0-190.8 124.1-292.1 246.2-292.1 65 0 119.1 42.7 159.7 42.7 38.6 0 98.8-45.3 174.1-45.3 28.2 0 129.6 2.6 196.3 99.3zM554.1 159.4c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8.6 15.6 1.3 18.2 2.6.6 6.5 1.3 10.4 1.3 45.3 0 103.7-30.4 139.3-71.4z" />
    </svg>
  );
}

export default observer(LoginPage);
